import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Copy } from "lucide-react";
import { Button } from "../ui/Button";

const COPY_OPTIONS = [
  { key: "all", label: "Copy all patient IDs" },
  { key: "with-job", label: "Copy with job name" },
  { key: "no-job", label: "Copy with no job name" },
];

const filterJobs = (processingJobs, filter) => {
  const jobs = Array.isArray(processingJobs) ? processingJobs : [];

  if (filter === "with-job") {
    return jobs.filter((job) => Boolean(job?.job));
  }

  if (filter === "no-job") {
    return jobs.filter((job) => !job?.job);
  }

  return jobs;
};

const CopyProcessingPatientIdsMenu = ({ processingJobs, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [copiedLabel, setCopiedLabel] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleCopy = async (filter, label) => {
    const patientIds = filterJobs(processingJobs, filter)
      .map((job) => job?.patient_id)
      .filter(Boolean);

    if (patientIds.length === 0) {
      window.alert("No patient IDs to copy for this filter.");
      setOpen(false);
      return;
    }

    try {
      await navigator.clipboard.writeText(patientIds.join(", "));
      setCopiedLabel(label);
      setTimeout(() => setCopiedLabel(""), 2000);
    } catch {
      window.alert("Failed to copy to clipboard.");
    }

    setOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <Button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={disabled || !Array.isArray(processingJobs) || processingJobs.length === 0}
        className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
      >
        <Copy className="h-4 w-4" />
        {copiedLabel || "Copy patient IDs"}
        <ChevronDown className="h-4 w-4" />
      </Button>

      {open && (
        <div className="absolute right-0 z-10 mt-2 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {COPY_OPTIONS.map((option) => {
            const count = filterJobs(processingJobs, option.key).length;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => handleCopy(option.key, option.label)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
              >
                <span>{option.label}</span>
                <span className="text-xs text-gray-400">{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CopyProcessingPatientIdsMenu;
